// backup.service — jalankan backup (O/M only): dump → download lokal + upload server + retensi + audit.
import { currentUser } from '@core/session';
import { verifyOwnPassword } from '@modules/auth/auth.service';
import { downloadFile } from '@ui/download';
import { logAudit } from '@core/audit';
import { canExportDirect } from '@domain/exportPolicy';
import * as repo from './backup.repo';

export interface BackupResult { filename: string; tables: number; rows: number; cleaned: number; localOk: boolean }

/** `local=false` → hanya simpan ke server (dipakai auto-backup saat tutup toko: jangan
 *  memaksa unduhan file & dialog export di layar kasir). Manual tetap unduh salinan lokal. */
export async function runBackup(local = true, namaFile?: string): Promise<BackupResult> {
  const u = currentUser();
  // Guard export (file KELUAR dari mesin) hanya untuk backup manual. Snapshot server-only
  // (auto tutup kasir / pengaman sebelum restore) tak mengeluarkan file → cukup user login.
  if (!u) throw new Error('Sesi tidak valid.');
  if (local && !canExportDirect(u.role)) throw new Error('Backup dengan unduhan hanya untuk Owner/Manager.');
  const dump = await repo.dumpAll();
  // Tabel gagal terbaca = backup TIDAK LENGKAP → gagalkan sebelum simpan/retensi (backup parsial lebih bahaya).
  if (dump.skipped.length) throw new Error(`Backup GAGAL — tabel tak terbaca: ${dump.skipped.join(', ')}. TIDAK disimpan (cegah backup tak lengkap). Coba lagi.`);
  const meta = { app: 'iFiCs', version: 1, by: u.nama, at: new Date().toISOString(), tables: Object.keys(dump.data).length, rows: dump.rows };
  const json = JSON.stringify({ meta, data: dump.data });
  const filename = namaFile ?? `ifics-backup-${new Date().toISOString().slice(0, 10)}.json`; // harian 1/hari (upsert timpa)
  await repo.uploadBackup(filename, json);                            // server (Supabase Storage) — sumber utama
  const localOk = local ? await downloadFile(json, filename, 'application/json') : false; // lokal (guard export; O/M lolos)
  const cleaned = await repo.cleanupOld(30);                          // retensi 30 hari (waktu server)
  void logAudit({ action: 'BACKUP', module: 'backup', refId: `${filename} · ${meta.tables} tabel · ${meta.rows} baris · lokal ${local ? (localOk ? 'ok' : 'GAGAL') : '-'} · hapus ${cleaned} lama` });
  return { filename, tables: meta.tables, rows: dump.rows, cleaned, localOk };
}

export interface RestoreResult { rows: number; tables: Record<string, number>; safety: string }

/**
 * RESTORE PENUH — Owner saja (ditegakkan juga di dalam RPC DB).
 * Selalu mengambil BACKUP PENGAMAN lebih dulu: restore menimpa seluruh data, dan tanpa
 * titik-balik satu klik keliru = kehilangan permanen.
 */
export async function runRestore(json: string, password: string): Promise<RestoreResult> {
  const u = currentUser();
  if (!u || u.role !== 'Owner') throw new Error('Restore data hanya oleh Owner.');
  // Bukti kedua: peran saja tak cukup untuk aksi yang menimpa SELURUH data. Token sesi Owner yang
  // terlanjur dicuri (mis. lewat perangkat yang ditinggal terbuka) sudah lolos pemeriksaan peran —
  // password memaksa penyerang memiliki sesuatu yang tidak ada di penyimpanan peramban.
  // Pola yang sama dipakai hapus tiket oleh Owner dan Reset Data.
  await verifyOwnPassword(password, 'Password salah — data tidak dipulihkan.');
  let parsed: { meta?: unknown; data?: Record<string, unknown[]> };
  try { parsed = JSON.parse(json) as typeof parsed; } catch { throw new Error('File bukan JSON backup iFiCs yang valid.'); }
  const payload = parsed.data;
  if (!payload || typeof payload !== 'object' || !Object.keys(payload).length) {
    throw new Error('Isi backup kosong / bukan format iFiCs (butuh field "data").');
  }
  // Backup pengaman SEBELUM menimpa (server-only, tanpa unduhan) — bisa dipakai membatalkan.
  // Nama UNIK: kalau memakai nama harian, backup pengaman menimpa backup sah hari itu — dan
  // restore kedua di hari sama menimpa pengaman pertama dgn state yang salah = titik-balik hilang.
  const safety = await runBackup(false, `ifics-safety-${new Date().toISOString().replace(/[:.]/g, '-')}.json`);
  const res = await repo.restoreAll(payload as Record<string, unknown>);
  void logAudit({ action: 'RESTORE', module: 'backup', refId: `${res.rows} baris`, reason: `pengaman: ${safety.filename}` });
  return { ...res, safety: safety.filename };
}
